import { Box } from '@mui/material';
import IdentityCard from 'components/@home/@settings/Tabs/Agents/IdentityCard';
import InstructionEditor from 'components/@home/@settings/Tabs/Agents/InstructionEditor';
import InstructionSaveFooter from 'components/@home/@settings/Tabs/Agents/InstructionSaveFooter';
import TextBoxOutlineIcon from 'mdi-react/TextBoxOutlineIcon';
import { arrayOf, object, oneOf } from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { attachedSkillsForAgent, unionToolsFromSkills } from 'utils/skillToolsUnion';
import { I18n } from 'utils/i18n';

export const INSTRUCTION_KINDS = ['base', 'tags', 'summary'];

const ENTITY_TYPE_FOR = {
  base: 'agent_base',
  tags: 'tags',
  summary: 'summary',
};

const TITLE_KEY_FOR = {
  base: 'InstructionBaseTitle',
  tags: 'InstructionTagsTitle',
  summary: 'InstructionSummaryTitle',
};

const HELPER_KEY_FOR = {
  base: 'InstructionBaseHelper',
  tags: 'InstructionTagsHelper',
  summary: 'InstructionSummaryHelper',
};

const InstructionsPanel = ({ agent, skills, tools, kind = 'base' }) => {
  const safeKind = INSTRUCTION_KINDS.includes(kind) ? kind : 'base';
  const entityType = ENTITY_TYPE_FOR[safeKind];
  const attachedSkills = attachedSkillsForAgent(agent, skills);
  const { tools: availableTools } = unionToolsFromSkills(attachedSkills, tools);
  const availableSkills = attachedSkills;

  const instructionEditorRef = useRef(null);
  const [instructionDirty, setInstructionDirty] = useState(false);
  const [instructionSaveFeedback, setInstructionSaveFeedback] = useState(null);
  const saveFeedbackTimerRef = useRef(null);

  const clearSaveFeedbackLater = useCallback(() => {
    if (saveFeedbackTimerRef.current) clearTimeout(saveFeedbackTimerRef.current);
    saveFeedbackTimerRef.current = setTimeout(() => setInstructionSaveFeedback(null), 2400);
  }, []);

  useEffect(
    () => () => {
      if (saveFeedbackTimerRef.current) clearTimeout(saveFeedbackTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    if (instructionDirty && instructionSaveFeedback?.severity === 'success') {
      setInstructionSaveFeedback(null);
    }
  }, [instructionDirty, instructionSaveFeedback?.severity]);

  const handleInstructionFooterSave = useCallback(() => {
    instructionEditorRef.current?.openSaveDialog?.();
  }, []);

  return (
    <Box
      sx={{
        margin: '0 auto',
        pt: 3,
        px: 4,
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        width: '100%',
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <IdentityCard
          icon={<TextBoxOutlineIcon size={22} />}
          title={I18n.t(`AgenticChatFlow.${TITLE_KEY_FOR[safeKind]}`)}
          description={I18n.t(`AgenticChatFlow.${HELPER_KEY_FOR[safeKind]}`)}
          divider
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <InstructionEditor
          key={safeKind}
          ref={instructionEditorRef}
          entityType={entityType}
          entityId={agent._id}
          availableTools={safeKind === 'base' ? availableTools : []}
          availableSkills={safeKind === 'base' ? availableSkills : []}
          parentManagedSave
          onDirtyChange={setInstructionDirty}
          onSaved={() => {
            setInstructionSaveFeedback({
              severity: 'success',
              message: I18n.t('AgenticChatFlow.ToolSaved'),
            });
            clearSaveFeedbackLater();
          }}
        />
      </Box>
      <InstructionSaveFooter
        dirty={instructionDirty}
        saveFeedback={instructionSaveFeedback}
        onSave={handleInstructionFooterSave}
      />
    </Box>
  );
};

InstructionsPanel.propTypes = {
  agent: object.isRequired,
  skills: arrayOf(object).isRequired,
  tools: arrayOf(object).isRequired,
  kind: oneOf(INSTRUCTION_KINDS),
};

export default InstructionsPanel;
